import { type ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import {
    chakra,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Text,
    Switch,
    FormHelperText,
    useMediaQuery,
} from '@chakra-ui/react';
import { Select, type SingleValue } from 'chakra-react-select';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import styled from '@emotion/styled';

import { useDealTitleValidate } from '../../../api/hooks/useDealTitleValidate';
import { type DealNameValidateResponse } from '../../../api/services/deal';
import { HtmlFormInput } from '../../hooks/useHtmlFormInputs';
import { type TemplateOption } from '../../hooks/useProvideDetails';

export type TemplateTitleFieldProps = {
    nameInput: HtmlFormInput<string>;
    dealNameTemplateInput: HtmlFormInput<number | null>;
    options: TemplateOption[];
};

type FullNameOfDealProps = {
    name: string;
    choosedOption?: TemplateOption;
};

type DealNameTemplateTitleFieldProps = {
    input: HtmlFormInput<number | null>;
    options: TemplateOption[];
    onChange: CallableFunction;
};

type TitleFieldProps = {
    input: HtmlFormInput<string>;
    isStandalone: Boolean;
    onChange: CallableFunction;
    disabled?: Boolean;
};

export function TemplateTitleField({
    nameInput,
    dealNameTemplateInput,
    options,
}: TemplateTitleFieldProps) {
    const [, { language }] = useTranslation();
    const { setValue, clearErrors } = useFormContext();
    const [name, setName] = useState<string>(nameInput.defaultValue);
    const [choosedOption, setChoosedOption] = useState<TemplateOption | undefined>();
    const [isOnlyName, setIsOnlyName] = useState<Boolean>(
        () => nameInput.defaultValue !== '' && dealNameTemplateInput.defaultValue === null,
    );

    useEffect(() => {
        setChoosedOption(
            options.find(
                (option) =>
                    String(dealNameTemplateInput.input.current.value) === String(option.value),
            ),
        );
    }, [options]);

    useEffect(() => {
        setName(nameInput.input.current.value);
    }, [nameInput.input.current.value]);

    const onChangeSwitch = (e: any) => {
        setIsOnlyName(e.target.checked);
        clearErrors(['deal_name_template', 'name']);
        setValue('name', '');
        nameInput.onChange('');
        if (e.target.checked === true) {
            setValue('deal_name_template', null);
            dealNameTemplateInput.onChange(null);
            setChoosedOption(undefined);
        }
    };

    return (
        <>
            {options.length > 0 && isOnlyName === false ? (
                <Grid templateColumns={{ md: '1fr 1fr', base: '1fr' }} gap={4}>
                    <DealNameTemplateTitleField
                        input={dealNameTemplateInput}
                        options={options}
                        onChange={setChoosedOption}
                    />
                    <TitleField
                        input={nameInput}
                        isStandalone={false}
                        onChange={useDebouncedCallback(setName, 200)}
                        disabled={choosedOption === undefined}
                    />
                </Grid>
            ) : (
                <TitleField
                    input={nameInput}
                    isStandalone={true}
                    onChange={useDebouncedCallback(setName, 200)}
                />
            )}

            {options.length > 0 && (
                <FormControl variant="fastDetails" mt="4 !important">
                    <Switch
                        onChange={onChangeSwitch}
                        width="fit-content"
                        defaultChecked={isOnlyName === true}
                    >
                        Chcę wpisać swój tytuł (to może wydłużyć weryfikację).
                    </Switch>
                </FormControl>
            )}

            {language === 'pl' && <FullNameOfDeal name={name} choosedOption={choosedOption} />}
        </>
    );
}

const FullNameOfDeal = ({ name, choosedOption }: FullNameOfDealProps) => {
    const fullName = useMemo(() => {
        if (choosedOption !== undefined && name !== '') return `${choosedOption.label} ${name}`;
        else if (choosedOption !== undefined) return choosedOption.label ?? '';
        else if (name !== '') return name;
        return '';
    }, [name, choosedOption]);

    if (fullName === '') return <></>;

    return (
        <Text color="general.paragraph" mt="6 !important">
            Tak będzie wyglądał tytuł na fakturze:{' '}
            <chakra.span fontWeight="bold">{fullName}</chakra.span>
        </Text>
    );
};

const DealNameTemplateTitleField = ({
    input,
    options,
    onChange,
}: DealNameTemplateTitleFieldProps) => {
    const { control } = useFormContext();
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isTouch] = useMediaQuery('(pointer: coarse)');

    const {
        field: { onChange: onChangeTemplate, value, ...templateFieldProps },
        fieldState: { error },
    } = useController({
        name: 'deal_name_template',
        control,
        rules: {
            required: t('common.errors.required'),
        },
    });

    const handleOnChange = (option: SingleValue<TemplateOption>) => {
        onChangeTemplate(option?.value ?? null);
        input.onChange(option?.value ?? null);
        onChange(option);
        setIsOpen(false);
    };

    return (
        <FormControl variant="fastDetails" isInvalid={error !== undefined}>
            <FormLabel>{t('fast.provide_details.title.label')}</FormLabel>
            <Select
                {...templateFieldProps}
                menuIsOpen={isTouch ? isOpen : undefined}
                onMenuOpen={isTouch ? () => setIsOpen(!isOpen) : undefined}
                onMenuClose={isTouch ? () => {} : undefined}
                value={options.find(
                    (option) => String(input.input.current.value) === String(option.value),
                )}
                placeholder={t('fast.provide_details.template_select.placeholder')}
                noOptionsMessage={() => t('fast.provide_details.template_select.no_options')}
                onChange={handleOnChange}
                options={options}
                chakraStyles={{
                    control: (provided) => ({
                        ...provided,
                        height: '6.4rem',
                    }),

                    inputContainer: (provided) => ({
                        ...provided,
                        maxW: '450px',
                    }),

                    placeholder: (provided) => ({
                        ...provided,
                        marginBottom: '2px',
                    }),

                    option: (provided) => ({
                        ...provided,
                        height: '4rem',
                        color: 'general.font',
                        bg: 'transparent',
                        _hover: {
                            color: 'general.font',
                            bg: 'general.menuGray',
                        },
                    }),
                }}
            />
            <FormErrorMessage>{error?.message}</FormErrorMessage>
        </FormControl>
    );
};

const TitleField = ({ input, isStandalone, onChange, disabled }: TitleFieldProps) => {
    const { control } = useFormContext();
    const { t } = useTranslation();
    const [, { language }] = useTranslation();
    const { validate } = useDealTitleValidate();
    const [alert, setAlert] = useState('');

    const {
        field: { onChange: onChangeName, ...nameFieldProps },
        fieldState: { error },
    } = useController({
        name: 'name',
        control,
        rules: {
            required: isStandalone === true ? t('common.errors.required') : false,
            maxLength: 150,
        },
    });

    const validateTitle = useDebouncedCallback((title) => {
        validate({ title })
            .then((res: DealNameValidateResponse) => {
                setAlert(res.message);
            })
            .catch((err: any) => console.log(err));
    }, 500);

    const handleOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        onChangeName(e);
        onChange(e.target.value);
        input.onChange(e.target.value);
        validateTitle(e.target.value);
    };

    return (
        <FormControl variant="fastDetails" isInvalid={error !== undefined}>
            <FormLabel visibility={isStandalone === true ? 'visible' : 'hidden'}>
                {t('fast.provide_details.title.label')}
            </FormLabel>
            <Input
                {...nameFieldProps}
                isDisabled={disabled === true}
                onChange={handleOnChange}
                placeholder={
                    isStandalone === false && language === 'pl'
                        ? 'Twój dopisek (opcjonalnie), np. Projekt dla...'
                        : ''
                }
            />
            {(language !== 'pl' || isStandalone === true) && (
                <FormHelperText>{t('fast.provide_details.title.helper_text')}</FormHelperText>
            )}
            <FormErrorMessage>{error?.message}</FormErrorMessage>
            {alert !== '' && (
                <AlertText fontSize="sm" color="orange" fontWeight="bold" mt={4}>
                    <span dangerouslySetInnerHTML={{ __html: alert }} />
                </AlertText>
            )}
        </FormControl>
    );
};

const AlertText = styled(Text)`
    font-size: 1.4rem;
    color: var(--ck-colors-general-orange);
    font-weight: bold;
    line-height: 2.1rem;
    margin-top: var(--ck-space-4);

    a {
        text-decoration: underline;
    }
`;
